import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle, 
  Cog, 
  Database, 
  Cpu, 
  Settings,
  Factory,
  TrendingUp,
  Users,
  Phone,
  Mail,
  MapPin,
  Calendar
} from 'lucide-react';
import './App.css';

import psg1 from './assets/IP8.jpeg';
import psg2 from './assets/IP1.jpeg';
import psg3 from './assets/IP10.jpeg';

const App = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-gray-900">
                Improve<span className="text-primary-600">Process</span>
              </span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-gray-700 hover:text-primary-600 transition-colors">Serviços</a>
              <a href="#cases" className="text-gray-700 hover:text-primary-600 transition-colors">Casos de Sucesso</a>
              <a href="#about" className="text-gray-700 hover:text-primary-600 transition-colors">A Nossa Equipa</a>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Contactar
              </motion.button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={require('./assets/IP9.jpeg')}
            alt="Industrial Automation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/70"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Transforme o seu Negócio com
              <span className="block text-primary-400">Automação Inteligente</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto">
              nós temos para si soluções personalizadas em automação, robótica e análise de dados que
              elevam a sua empresa da indústria tradicional para a indústria 4.0
            </p>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white"
        >
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </motion.div>
      </section>

      {/* Services Section - 4 Phase Methodology */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Aplicamos uma Metodologia de <span className="text-primary-600">5 Fases</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cada uma delas é um processo contínuo e cíclico de co-criação com o cliente,<br></br>
              que inclui testes e validação no contexto real de trabalho.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              {
                phase: "01",
                title: "Avaliação de Necessidades",
                description: "Fazemos uma análise detalhada dos seus processos atuais e identificamos as oportunidades de melhoria",
                icon: <Database className="w-8 h-8" />,
                color: "from-primary-400 to-primary-600"
              },
              {
                phase: "02", 
                title: "Investigação & Desenvolvimento",
                description: "Desenvolvemos soluções personalizadas alinhadas com as melhores referências de inovação industrial",
                icon: <Cpu className="w-8 h-8" />,
                color: "from-primary-400 to-primary-600"
              },
              {
                phase: "03",
                title: "Implementação",
                description: "Integramos soluções de automação e aquisição de dados orientados para a era da inteligência artificial",
                icon: <Settings className="w-8 h-8" />,
                color: "from-primary-400 to-primary-600"
              },
              {
                phase: "04",
                title: "Documentação & Formação",
                description: "Damos formação técnica e entregamos documentação completa para uma autonomia total da sua equipa e empresa",
                icon: <Users className="w-8 h-8" />,
                color: "from-primary-400 to-primary-600"
              },
              {
                phase: "05",
                title: "Assistência Técnica",
                description: "Disponibilizamos soluções de suporte contínuo no tempo e de manutenção industrial",
                icon: <CheckCircle className="w-8 h-8" />,
                color: "from-primary-400 to-primary-600"
              }
            ].map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group-hover:border-primary-200">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${phase.color} flex items-center justify-center text-white mb-6`}>
                    {phase.icon}
                  </div>
                  <div className="text-4xl font-bold text-gray-300 mb-2">{phase.phase}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{phase.title}</h3>
                  <p className="text-gray-600">{phase.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Transformation Section */}
      <section className="py-20 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={require('./assets/IP3.jpeg')}
            alt="Industrial Manufacturing Automation"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Da Indústria Tradicional para a <span className="text-primary-400">Indústria 4.0</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Seguimos as melhores práticas Europeias e utilizamos marcas de referência 
              para promovermos a inovação e progresso na indústria Portuguesa
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                level: "Indústria 2.0",
                title: "Automatização Básica",
                description: "Sistemas de produção com controlo básico e electromecânicos simples",
                progress: 30
              },
              {
                level: "Indústria 3.0", 
                title: "Automatização de Processos",
                description: "Sistemas electrónicos de controlo digital, PLC's, computadores e robótica",
                progress: 65
              },
              {
                level: "Indústria 4.0",
                title: "Automação e Digitalização",
                description: "Análise de dados com finalidade preditiva para posterior integração com I.A.",
                progress: 100
              }
            ].map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
              >
                <h3 className="text-2xl font-bold text-primary-400 mb-2">{industry.level}</h3>
                <h4 className="text-xl font-semibold mb-4">{industry.title}</h4>
                <p className="text-gray-300 mb-6">{industry.description}</p>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${industry.progress}%` }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-r from-primary-400 to-primary-600 h-2 rounded-full"
                  ></motion.div>
                </div>
                <div className="text-right mt-2 text-primary-400 font-semibold">{industry.progress}%</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="cases" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Casos de <span className="text-primary-600">Sucesso</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Projetos reais com impactos mensuráveis nos mais diversos sectores industriais
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Agro-Indústria",
                subtitle: "Sistema de Alimentação e Controlo de Peso Automatizado",
                period: "2022 - Presente",
                description: "Desenvolvemos um sistema de automação para alimentação de gado bovino a farinha, capaz de gerir a identidade dos animais, registar o seu peso e fornecer dietas personalizadas conforme as suas necessidades individuais.",
                results: ["Funcionamento contínuo 24h/dia, 365 dias/ano", "Monitorização de ração, peso, e consumo de água", "Interface simples para monitorização do crescimento animal"],
                image: psg1,
                status: "Concluído"
              },
              {
                title: "Indústria de Materiais",
                subtitle: "Linha de Produção de Compostos Cerâmicos",
                period: "2023 - Presente", 
                description: "Desenvolvimento de um sistema de controlo de pesos e matérias primas para uma linha de produção de pastas para peças cerâmicas, permitindo a criação precisa de receitas compósitas de grande dimensão.",
                results: ["Capacidade de produção de receitas de várias toneladas", "Precisão de cada ingrediente com um erro inferior a 2%", "Redução de erros de produção e digitalização de todo o processo produtivo", "Maior flexibilidade operacional e maior controlo de lotes"],
                image: psg2,
                status: "Concluído"
              },
              {
                title: "Indústria Transformadora",
                subtitle: "Recolha de Informação Industrial",
                period: "2025 - Presente",
                description: "Integração tecnológica com líder na produção de prateleiras industriais e domésticas, para recolha de dados de produção em tempo real.",
                results: ["Equipamentos com +20 anos modernizados", "Standardização do controlo de processos", "Análise de dados em tempo real", "Decisões de produção mais rápidas"],
                image: psg3,
                status: "Concluído"
              }
            ].map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {project.status}
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">{project.title}</h3>
                    <span className="text-sm text-gray-500">{project.period}</span>
                  </div>
                  <h4 className="text-lg font-semibold text-primary-600 mb-4">{project.subtitle}</h4>
                  <p className="text-gray-600 mb-6">{project.description}</p>
                  <div className="space-y-2">
                    <h5 className="font-semibold text-gray-900">Resultados:</h5>
                    {project.results.map((result, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary-600" />
                        <span className="text-gray-700 text-sm">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Nossa <span className="text-primary-600">Equipa</span>
              </h2>
              <div className="space-y-6">
                <p className="text-lg text-gray-600 leading-relaxed">
                  Na Improve Process a equipa de desenvolvimento é criada especificamente para cada projecto de acordo com as características únicas de cada cliente, procuramos aplicar na engenharia o mote{' '}
                  <span className="font-semibold text-primary-600">"Do More with Less for More"</span>, e assim chegar a mais resultados e a melhores processos do que o ponto de partida.
                </p>
                <div className="bg-gray-50 rounded-2xl p-8 border-l-4 border-primary-600">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-primary-100 rounded-full p-3">
                      <Users className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Abordagem Personalizada</h3>
                  </div>
                  <p className="text-gray-600">
                    Cada projeto recebe uma equipa multidisciplinar especificamente montada para as suas necessidades únicas, garantindo soluções verdadeiramente personalizadas e eficazes.
                  </p>
                </div>
                <div className="bg-primary-50 rounded-2xl p-8 border-l-4 border-primary-600">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-primary-100 rounded-full p-3">
                      <TrendingUp className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Filosofia "Do More with Less for More"</h3>
                  </div>
                  <p className="text-gray-600">
                    A nossa abordagem de engenharia foca em maximizar resultados através de soluções inteligentes e eficientes, superando sempre o ponto de partida dos nossos clientes.
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src= {require('./assets/IP5.jpeg')}
                  alt="Equipa de engenharia profissional da Improve Process"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/20 to-transparent"></div>
              </div>
              
              {/* Floating stats cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl border border-gray-100"
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-1">4</div>
                  <div className="text-sm text-gray-600">Sectores de Atuação</div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                className="absolute -top-6 -right-6 bg-white rounded-xl p-6 shadow-xl border border-gray-100"
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-1">100%</div>
                  <div className="text-sm text-gray-600">Soluções Personalizadas</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}  
      <section className="py-20 bg-primary-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={require('./assets/IP3.jpeg')}
            alt="Professional Consultation"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Pronto para Transformar o Seu Negócio?
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center mb-6">
                <span className="text-3xl font-bold">
                  Improve<span className="text-primary-400">Process</span>
                </span>
              </div>
              <p className="text-gray-300 mb-6 max-w-md">
                Especialistas em soluções de automação personalizadas que transformam empresas tradicionais 
                em organizações inteligentes e orientadas por dados.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-primary-400" />
                  <span className="text-gray-300">Porto de Mós, Leiria, Portugal</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-primary-400" />
                  <span className="text-gray-300">geral@improve-process.pt</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">Serviços</h4>
              <ul className="space-y-3 text-gray-300">
                <li><a href="#" className="hover:text-primary-400 transition-colors">Automação Industrial</a></li>
                <li><a href="#" className="hover:text-primary-400 transition-colors">Sistemas Robóticos</a></li>
                <li><a href="#" className="hover:text-primary-400 transition-colors">Aquisição de Dados</a></li>
                <li><a href="#" className="hover:text-primary-400 transition-colors">Análise Inteligente</a></li>
                <li><a href="#" className="hover:text-primary-400 transition-colors">Formação Técnica</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">Empresa</h4>
              <ul className="space-y-3 text-gray-300">
                <li><a href="#about" className="hover:text-primary-400 transition-colors">Sobre Nós</a></li>
                <li><a href="#cases" className="hover:text-primary-400 transition-colors">Casos de Sucesso</a></li>
                <li><a href="#" className="hover:text-primary-400 transition-colors">Carreiras</a></li>
                <li><a href="#contact" className="hover:text-primary-400 transition-colors">Contacto</a></li>
                <li><a href="https://www.livroreclamacoes.pt/Inicio/" className="hover:text-primary-400 transition-colors">Livro de Reclamações</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-12 pt-8 text-center text-gray-400">
            <p> 2026. Improve Process. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;